import { Series, SeriesSet } from "../model/series";
import { OneExperienceNotice } from "./one_experience_notice";
import { DataDate } from "../model/data_date";
import { AgeGroupDateUtils } from "../model/age_group_date_utils";
import { downloadAsPng } from "../utils/graph_downloader";
import $ from "jquery";
import Raphael, { RaphaelElement, RaphaelPaper } from "raphael";

declare global {
  interface Window {
    Raphael: typeof Raphael;
  }
}

window.Raphael = Raphael; // Ensure Raphael is globally available
import("../vendor/graphael/g.raphael");
import("../vendor/graphael/g.line");
import("../vendor/jquery.tipsy");

interface RaphaelPaperWtihLine extends RaphaelPaper {
  linechart: any;
}

// Axis interfaces
interface AxisPolicy {
  new(seriesSet: SeriesSet): AxisInstance;
}

interface AxisInstance {
  step(): number;
  doesShowAllLabels(): boolean;
  fixLabels(axis: any, seriesSet?: SeriesSet): void;
  todayRatio?(): number;
}

// StatsGraph policies interface
interface StatsGraphPolicies {
  xAxis?: AxisPolicy;
  yAxis?: AxisPolicy;
  legend: LegendPolicy;
  colors?: string[];
  todayLine?: boolean;
}

interface LegendPolicy {
  new(graph: StatsGraph): LegendInstance;
}

interface LegendInstance {
  draw(): void;
}

class StatsGraph {
  // Constants
  private readonly CANVAS_WIDTH = 680;
  private readonly CANVAS_HEIGHT = 330;
  private readonly GRAPH_X = 30;
  private readonly GRAPH_Y = 10;
  private GRAPH_WIDTH = 640; // Note: can be modified by RightColumnLegend
  private readonly GRAPH_HEIGHT = 300;
  private readonly AXIS_SPACE = 10;

  private static readonly BASE_COLOR = "white";
  private static readonly DEFAULT_COLORS = [
    "#EC4A59",
    "#B8C12B",
    "#1F91CF",
    "#FCDA0F",
    "#C6487E",
    "#2B8863",
    "#F6A5AC",
    "#DCE095",
    "#8FC8E7",
    "#FEED87",
    "#E3A4BF",
    "#95C4B1",
  ];

  private static readonly CHART_LINE_WIDTH = 1;
  private static readonly CHART_SYMBOL_SIZE = 3;
  private static readonly AXIS_ATTRIBUTES = { stroke: "gray" };
  private static readonly NUM_DIVISIONS = 18;
  private static readonly DIVISION_ATTRIBUTES = {
    "stroke-width": 0,
    fill: "#ddd",
  };
  private static readonly DIVISION_WIDTH = 0.5;
  private static readonly DIVISION_STEP_ADJUST = -0.2;
  private static readonly TODAY_BAR_ATTRIBUTES = {
    "stroke-width": 0,
    fill: "#F59427",
  };
  private static readonly TODAY_BAR_WIDTH = 1;

  // Instance properties
  private canvas: JQuery;
  private seriesSet: SeriesSet;
  private xAxis: AxisInstance;
  private yAxis: AxisInstance;
  private legend: LegendInstance;
  private colors: string[];
  private enableTodayLine: boolean;
  private onRemoval: Array<(index: number, series: Series) => void>;
  private paper: RaphaelPaperWtihLine | null;
  private chart: any;

  // Static axis policies
  static AxisPolicy = {
    forPartition: (partition: string): AxisPolicy => {
      switch (partition) {
        case "daily":
          return StatsGraph.DailyAxis;
        case "weekly":
          return StatsGraph.WeeklyAxis;
        case "monthly":
          return StatsGraph.MonthlyAxis;
        case "yearly":
          return StatsGraph.YearlyAxis;
        default:
          throw new Error(`unknown partition: ${partition}`);
      }
    },
  };

  // Base axis policy class
  static BaseAxisPolicy = class implements AxisInstance {
    protected seriesSet: SeriesSet;

    constructor(seriesSet: SeriesSet) {
      this.seriesSet = seriesSet;
    }

    step(): number {
      return 1;
    }

    doesShowAllLabels(): boolean {
      return false;
    }

    fixLabels(_axis: any, _seriesSet?: SeriesSet): void {
      // Default implementation
    }

    protected allDataPointsStep(): number {
      const steps = this.seriesSet.get(0).size() - 1;
      return steps <= 0 ? 1 : steps;
    }

    protected startDate(axis: any): any {
      const time = axis.text.items[0].attr("text");
      return this.seriesSet.getDataDate(parseInt(time, 10));
    }

    protected setDataPointMonthDateLabels(axis: any): void {
      this.replaceAxisLabels(axis, (label: string) => {
        const d = this.seriesSet.getDataDate(parseInt(label, 10));
        return d.md();
      });
    }

    protected setAxisLabels(axis: any, labels: string[]): void {
      let i = -1;
      this.replaceAxisLabels(axis, (_label: string) => {
        i += 1;
        return labels[i];
      });
    }

    protected replaceAxisLabels(
      axis: any,
      transform: (text: string) => string,
    ): void {
      axis.text.items.forEach((label: any) => {
        const text = label.attr("text");
        if (text) {
          label.attr({ text: transform(text) });
        }
      });
    }
  };

  // Specific axis implementations
  static DailyAxis = class extends StatsGraph.BaseAxisPolicy {
    step(): number {
      return 53 - 1; // (number of weeks in a year) - 1
    }

    doesShowAllLabels(): boolean {
      return false;
    }

    fixLabels(axis: any): void {
      this.setDataPointMonthDateLabels(axis);
    }

    todayRatio(): number {
      return 1 / 2;
    }
  };

  static WeeklyAxis = class extends StatsGraph.BaseAxisPolicy {
    step(): number {
      return this.allDataPointsStep();
    }

    doesShowAllLabels(): boolean {
      return this.step() < 15;
    }

    fixLabels(axis: any): void {
      if (this.doesShowAllLabels()) {
        this.setAxisLabels(axis, this.weekLabels(axis));
      } else {
        this.setDataPointMonthDateLabels(axis);
      }
    }

    private weekLabels(axis: any): string[] {
      let d = this.startDate(axis);
      const labels: string[] = [];
      for (let i = 0; i <= this.step(); i++) {
        labels.push(d.md());
        d = d.nextWeek();
      }
      return labels;
    }

    todayRatio(): number {
      return 26 / 51.5;
    }
  };

  static MonthlyAxis = class extends StatsGraph.BaseAxisPolicy {
    step(): number {
      return this.allDataPointsStep();
    }

    doesShowAllLabels(): boolean {
      return true;
    }

    fixLabels(axis: any): void {
      this.setAxisLabels(axis, this.monthLabels(axis));
    }

    private monthLabels(axis: any): string[] {
      let d = this.startDate(axis);
      const labels: string[] = [];
      for (let i = 0; i <= this.step(); i++) {
        labels.push(d.m());
        d = d.nextMonthStartDate();
      }
      return labels;
    }

    todayRatio(): number {
      return 6 / 11;
    }
  };

  static AgeGroupMonthlyAxis = class extends StatsGraph.BaseAxisPolicy {
    step(): number {
      // 常に36ヶ月分（35ステップ）を返す
      return 35;
    }

    doesShowAllLabels(): boolean {
      return true;
    }

    protected startDate(_axis: any): any {
      // SeriesSetのglobalOriginTime()を使うことで、Seriesのoriginと一致させる
      // これにより、個別グラフでもX軸とデータが正しくマッピングされる
      const seriesWithOrigin = this.seriesSet.list.find((series) => series.origin);
      if (seriesWithOrigin && seriesWithOrigin.origin) {
        return seriesWithOrigin.origin;
      }
      // フォールバック: 固定の日付を返す（convertToSeriesと一致させる）
      const startDate = AgeGroupDateUtils.getThreeYearsAgoStart();
      return new DataDate(startDate, "monthly");
    }

    fixLabels(axis: any): void {
      // まず月のラベルを設定
      const monthLabelData = this.monthLabelData(axis);
      this.setAxisLabels(axis, monthLabelData.monthLabels);

      // 年のラベルを月のラベルの下に追加
      this.addYearLabels(axis, monthLabelData.yearPositions);
    }

    private monthLabelData(axis: any): {
      monthLabels: string[];
      yearPositions: Array<{ index: number; year: number }>;
    } {
      let d = this.startDate(axis);
      let prevYear: number | null = null;
      const monthLabels: string[] = [];
      const yearPositions: Array<{ index: number; year: number }> = [];

      for (let i = 0; i <= this.step(); i++) {
        const currentYear = d.year();
        const currentMonth = d.month();

        // 年が変わった場合（1月の場合）は年のラベルを追加する位置を記録
        if (prevYear !== null && prevYear !== currentYear) {
          // 年が変わった場合、必ず1月のはず。月のラベルは表示せず、年のラベルのみ表示
          monthLabels.push("");
          yearPositions.push({ index: i, year: currentYear });
        } else if (prevYear === null && currentMonth === 1) {
          // 最初の月が1月の場合も年のラベルを追加。月のラベルは表示せず、年のラベルのみ表示
          monthLabels.push("");
          yearPositions.push({ index: i, year: currentYear });
        } else if (prevYear === null) {
          // 最初の月が1月でない場合は月のみ表示
          if (currentMonth % 2 === 0) {
            monthLabels.push(`${currentMonth}月`);
          } else {
            monthLabels.push("");
          }
        } else {
          // それ以外は偶数月のみ表示、奇数月は空文字列
          if (currentMonth % 2 === 0) {
            monthLabels.push(`${currentMonth}月`);
          } else {
            monthLabels.push("");
          }
        }

        prevYear = currentYear;
        d = d.nextMonthStartDate();
      }
      return { monthLabels, yearPositions };
    }

    private addYearLabels(axis: any, yearPositions: Array<{ index: number; year: number }>): void {
      if (!axis.text || !axis.text.items || yearPositions.length === 0) {
        return;
      }

      // 最初のラベルからpaperを取得
      const firstLabel = axis.text.items[0];
      if (!firstLabel || !firstLabel.paper) {
        return;
      }
      const paper = firstLabel.paper;

      yearPositions.forEach(({ index, year }) => {
        // 対応する月のラベルを取得
        const monthLabel = axis.text.items[index];
        if (!monthLabel) {
          return;
        }

        // 月のラベルの位置を取得
        let monthX: number;
        let monthY: number;

        const monthBBox = monthLabel.getBBox();

        // 空文字列のラベルの場合、getBBox()が正しく動作しない可能性があるため、
        // 前後のラベルの位置を参考にする
        if (monthBBox.width === 0 || monthBBox.height === 0) {
          // 前後のラベルから位置を推定
          let foundX = false;
          let prevX = 0;
          let prevIndex = -1;
          let nextX = 0;
          let nextIndex = -1;

          // 前のラベルを探す
          for (let i = index - 1; i >= 0; i--) {
            const prevLabel = axis.text.items[i];
            if (prevLabel) {
              const prevBBox = prevLabel.getBBox();
              if (prevBBox.width > 0 && prevBBox.height > 0) {
                prevX = prevBBox.x + prevBBox.width / 2;
                prevIndex = i;
                foundX = true;
                break;
              }
            }
          }

          // 後のラベルを探す
          for (let i = index + 1; i < axis.text.items.length; i++) {
            const nextLabel = axis.text.items[i];
            if (nextLabel) {
              const nextBBox = nextLabel.getBBox();
              if (nextBBox.width > 0 && nextBBox.height > 0) {
                nextX = nextBBox.x + nextBBox.width / 2;
                nextIndex = i;
                break;
              }
            }
          }

          if (foundX && nextIndex > 0) {
            // 前後のラベルの間で補間
            const ratio = (index - prevIndex) / (nextIndex - prevIndex);
            monthX = prevX + (nextX - prevX) * ratio;
          } else if (foundX) {
            monthX = prevX;
          } else {
            return; // 位置を推定できない場合はスキップ
          }

          monthY = monthBBox.y + monthBBox.height / 2;
        } else {
          monthX = monthBBox.x + monthBBox.width / 2;
          monthY = monthBBox.y + monthBBox.height / 2;
        }

        // 年のラベルを月のラベルの下に追加
        const yearText = paper.text(monthX, monthY + 15, `${year}年`);
        yearText.attr({
          "font-size": "10px",
          fill: "#666",
        });
      });
    }
  };

  // trend_research用の月次X軸（5年分のデータに対応）
  static TrendResearchMonthlyAxis = class extends StatsGraph.BaseAxisPolicy {
    step(): number {
      // データの範囲に基づいてステップ数を計算（最大5年分 = 60ヶ月 = 59ステップ）
      return this.allDataPointsStep();
    }

    doesShowAllLabels(): boolean {
      return true;
    }

    protected startDate(_axis: any): any {
      // SeriesSetのglobalOriginTime()を使うことで、Seriesのoriginと一致させる
      const seriesWithOrigin = this.seriesSet.list.find((series) => series.origin);
      if (seriesWithOrigin && seriesWithOrigin.origin) {
        return seriesWithOrigin.origin;
      }
      // フォールバック: 最初のSeriesの最初の日付を使用
      if (!this.seriesSet.isEmpty()) {
        const firstSeries = this.seriesSet.get(0);
        if (firstSeries && !firstSeries.isEmpty()) {
          const dates = firstSeries.dates();
          if (dates.length > 0) {
            return dates[0];
          }
        }
      }
      // 最終フォールバック: 固定の日付を返す
      return new DataDate(new Date(2021, 0, 1), "monthly");
    }

    fixLabels(axis: any): void {
      // AgeGroupMonthlyAxisと同じロジックを使用（年と月を別々に表示）
      const monthLabelData = this.monthLabelData(axis);
      this.setAxisLabels(axis, monthLabelData.monthLabels);
      this.addYearLabels(axis, monthLabelData.yearPositions);
    }

    private monthLabelData(axis: any): {
      monthLabels: string[];
      yearPositions: Array<{ index: number; year: number }>;
    } {
      let d = this.startDate(axis);
      let prevYear: number | null = null;
      const monthLabels: string[] = [];
      const yearPositions: Array<{ index: number; year: number }> = [];
      // 現在の年を取得（2026年など未来の年は表示しない）
      const currentYear = new Date().getFullYear();

      for (let i = 0; i <= this.step(); i++) {
        const currentYearValue = d.year();
        const currentMonth = d.month();

        // 現在の年を超える月は処理しない
        if (currentYearValue > currentYear) {
          break;
        }

        // 年が変わった場合（1月の場合）は年のラベルを追加する位置を記録
        if (prevYear !== null && prevYear !== currentYearValue) {
          // 年が変わった場合、必ず1月のはず。月のラベルは表示せず、年のラベルのみ表示
          monthLabels.push("");
          yearPositions.push({ index: i, year: currentYearValue });
        } else if (prevYear === null && currentMonth === 1) {
          // 最初の月が1月の場合も年のラベルを追加。月のラベルは表示せず、年のラベルのみ表示
          monthLabels.push("");
          yearPositions.push({ index: i, year: currentYearValue });
        } else if (prevYear === null) {
          // 最初の月が1月でない場合は月のみ表示
          if (currentMonth % 2 === 0) {
            monthLabels.push(`${currentMonth}`);
          } else {
            monthLabels.push("");
          }
        } else {
          // それ以外は偶数月のみ表示、奇数月は空文字列
          if (currentMonth % 2 === 0) {
            monthLabels.push(`${currentMonth}`);
          } else {
            monthLabels.push("");
          }
        }

        prevYear = currentYearValue;
        d = d.nextMonthStartDate();
      }
      return { monthLabels, yearPositions };
    }

    private addYearLabels(axis: any, yearPositions: Array<{ index: number; year: number }>): void {
      if (!axis.text || !axis.text.items || yearPositions.length === 0) {
        return;
      }

      // 最初のラベルからpaperを取得
      const firstLabel = axis.text.items[0];
      if (!firstLabel || !firstLabel.paper) {
        return;
      }
      const paper = firstLabel.paper;

      // 現在の年を取得（2026年など未来の年は表示しない）
      const currentYear = new Date().getFullYear();

      yearPositions.forEach(({ index, year }) => {
        // 現在の年を超える年のラベルは表示しない
        if (year > currentYear) {
          return;
        }

        // 対応する月のラベルを取得
        const monthLabel = axis.text.items[index];
        if (!monthLabel) {
          return;
        }

        // 月のラベルの位置を取得
        let monthX: number;
        let monthY: number;

        const monthBBoxForPosition = monthLabel.getBBox();

        // 空文字列のラベルの場合、getBBox()が正しく動作しない可能性があるため、
        // 前後のラベルの位置を参考にする
        if (monthBBoxForPosition.width === 0 || monthBBoxForPosition.height === 0) {
          // 前後のラベルから位置を推定
          let foundX = false;
          let prevX = 0;
          let prevIndex = -1;
          let nextX = 0;
          let nextIndex = -1;

          // 前のラベルを探す
          for (let i = index - 1; i >= 0; i--) {
            const prevLabel = axis.text.items[i];
            if (prevLabel) {
              const prevBBox = prevLabel.getBBox();
              if (prevBBox.width > 0 && prevBBox.height > 0) {
                prevX = prevBBox.x + prevBBox.width / 2;
                prevIndex = i;
                foundX = true;
                break;
              }
            }
          }

          // 後のラベルを探す
          for (let i = index + 1; i < axis.text.items.length; i++) {
            const nextLabel = axis.text.items[i];
            if (nextLabel) {
              const nextBBox = nextLabel.getBBox();
              if (nextBBox.width > 0 && nextBBox.height > 0) {
                nextX = nextBBox.x + nextBBox.width / 2;
                nextIndex = i;
                break;
              }
            }
          }

          if (foundX && prevIndex >= 0 && nextIndex >= 0) {
            // 前後のラベルの間で補間
            const ratio = (index - prevIndex) / (nextIndex - prevIndex);
            monthX = prevX + (nextX - prevX) * ratio;
          } else if (foundX) {
            monthX = prevX;
          } else {
            return; // 位置を推定できない場合はスキップ
          }

          monthY = monthBBoxForPosition.y + monthBBoxForPosition.height / 2;
        } else {
          monthX = monthBBoxForPosition.x + monthBBoxForPosition.width / 2;
          monthY = monthBBoxForPosition.y + monthBBoxForPosition.height / 2;
        }

        // 年のラベルを月のラベルの下に追加（X軸の下部に配置）
        // monthYは月のラベルの中央Y座標なので、月のラベルの下に配置するには、monthY + 月のラベルの高さ/2 + 余白
        // AgeGroupMonthlyAxisと同じロジックを使用
        const yearLabelY = monthY + 15; // 月のラベルの下に15pxの余白を追加
        const yearText = paper.text(monthX, yearLabelY, `${year}年`);
        yearText.attr({
          "font-size": "10px",
          fill: "#666",
        });
      });
    }
  };

  static YearlyAxis = class extends StatsGraph.BaseAxisPolicy {
    step(): number {
      return this.allDataPointsStep();
    }

    doesShowAllLabels(): boolean {
      return true;
    }

    fixLabels(axis: any): void {
      this.setAxisLabels(axis, this.yearLabels(axis));
    }

    private yearLabels(axis: any): string[] {
      let d = this.startDate(axis);
      const labels: string[] = [];
      for (let i = 0; i <= this.step(); i++) {
        labels.push(d.y());
        d = d.nextYearStartDate();
      }
      return labels;
    }
  };

  static FloatAxis = class extends StatsGraph.BaseAxisPolicy {
    step(): number {
      return StatsGraph.NUM_DIVISIONS / 2;
    }

    doesShowAllLabels(): boolean {
      return true;
    }

    fixLabels(_axis: any): void {
      // noop
    }
  };

  static IntegerAxis = class extends StatsGraph.BaseAxisPolicy {
    step(): number {
      return StatsGraph.NUM_DIVISIONS / 2;
    }

    doesShowAllLabels(): boolean {
      return true;
    }

    fixLabels(axis: any): void {
      this.replaceAxisLabels(axis, (label: string) => {
        return parseInt(label, 10).toString();
      });
    }
  };

  constructor(canvas: JQuery, policies: StatsGraphPolicies) {
    this.canvas = canvas;
    this.seriesSet = new SeriesSet();
    // デフォルトのAxisを設定（提供されていない場合）
    this.xAxis = policies.xAxis
      ? new policies.xAxis(this.seriesSet)
      : new StatsGraph.MonthlyAxis(this.seriesSet);
    this.yAxis = policies.yAxis
      ? new policies.yAxis(this.seriesSet)
      : new StatsGraph.FloatAxis(this.seriesSet);
    this.legend = new policies.legend(this);
    this.colors = policies.colors || StatsGraph.DEFAULT_COLORS;
    this.enableTodayLine = policies.todayLine || false;
    this.onRemoval = [];
    this.paper = null;
    this.chart = null;
    this.initialize();
  }

  initialize(): void {
    // Override me on demand
  }

  isEmpty(): boolean {
    return this.seriesSet.isEmpty();
  }

  hasNoData(): boolean {
    return this.seriesSet.hasNoData();
  }

  hasSeries(name: string): boolean {
    return this.seriesSet.hasKey(name);
  }

  seriesCount(): number {
    return this.seriesSet.size();
  }

  getSeries(index: number): Series {
    return this.seriesSet.get(index);
  }

  addSeries(series: Series): boolean {
    if (this.seriesSet.size() >= this.numMaxSupportedSerieses()) {
      return false;
    }
    this.seriesSet.add(series);
    return true;
  }

  addSeriesSet(set: SeriesSet): void {
    set.each((series: Series) => {
      this.addSeries(series);
    });
  }

  onSeriesRemoval(handler: (index: number, series: Series) => void): void {
    this.onRemoval.push(handler);
  }

  removeSeries(series: Series): void {
    const index = this.seriesSet.remove(series);
    if (index !== null) {
      this.onRemoval.forEach((handler) => {
        handler(index, series);
      });
    }
  }

  /**
   * X軸のAxisポリシーを後から設定する
   * @param axisPolicy Axisポリシークラス
   */
  setXAxis(axisPolicy: AxisPolicy): void {
    this.xAxis = new axisPolicy(this.seriesSet);
  }

  numMaxSupportedSerieses(): number {
    return this.colors.length;
  }

  drawMessage(msg: string): void {
    const escapedMsg = $("<div>").text($.trim(msg)).html();
    this.canvas.html(`<div class='message'>${escapedMsg}</div>`);
  }

  drawErrorMessage(msg: string): void {
    const escapedMsg = $("<div>")
      .text($.trim(msg))
      .html()
      .replace(/\n/g, "<br/>");
    this.canvas.html(`<div class='error_message'>${escapedMsg}</div>`);
  }

  clear(): void {
    this.canvas.empty();
    const id = this.canvas.attr("id");
    if (id == null) {
      throw new Error(
        "Canvas element must have an ID for Raphael initialization.",
      );
    }
    this.paper = Raphael(
      id,
      this.CANVAS_WIDTH,
      this.CANVAS_HEIGHT,
    ) as RaphaelPaperWtihLine;
    OneExperienceNotice.delete();
  }

  drawAll(): void {
    this.clear();
    this.drawDivisions();
    if (this.enableTodayLine && (this.xAxis as any).todayRatio) {
      this.drawTodayLine((this.xAxis as any).todayRatio());
    }
    this.drawChart();
    this.drawLegends();
    if (
      !$(".diagram_title")
        .first()
        .text()
        .match(/SI値年度比較グラフ/)
    ) {
      this.drawOneExperienceLine();
      OneExperienceNotice.append(this.canvas, this.xAxis);
    }
  }

  drawChart(): void {
    this.chart = this.drawSerieses(this.seriesSet);
    this.fixAxis(this.chart, this.seriesSet);
    this.setTooltips(this.getChartSymbols(this.chart), this.seriesSet);
    // trend_researchesページのグラフのみ、今年から昨年の部分を破線にする
    if (this.canvas.closest("[data-trend-research]").length > 0) {
      this.makeCurrentYearToLastYearDashed();
      // 前月と前年同月のノードを二重丸で囲む
      this.highlightComparisonNodes();
    }
  }

  /**
   * 線形回帰の直線を描画
   * @param intercept 切片
   * @param slope 傾き（1ヶ月あたりの変化量）
   * @param startDate 開始データポイントの日付文字列（YYYY-MM-DD形式）
   * @param endDate 終了データポイントの日付文字列（YYYY-MM-DD形式）
   * @param annualSlope 年率換算の傾き（%）
   * @param seriesIndex 系列のインデックス（デフォルトは0）
   */
  drawLinearRegressionLine(
    intercept: number,
    slope: number,
    startDate: string,
    endDate: string,
    annualSlope: number,
    seriesIndex: number = 0,
  ): void {
    if (!this.paper || !this.chart || this.seriesSet.size() === 0) {
      return;
    }

    const series = this.seriesSet.get(seriesIndex);
    if (!series || series.size() === 0) {
      return;
    }

    // グラフのX軸の範囲を取得（データポイントの位置から）
    const symbols = this.chart.symbols[seriesIndex];
    if (!symbols || symbols.length === 0) {
      return;
    }

    // 日付からシンボルを検索
    let startSymbol: any = null;
    let endSymbol: any = null;
    let startIndex = -1;
    let endIndex = -1;

    series.dates().forEach((date, index) => {
      const dateStr = date.ymd();
      if (dateStr === startDate) {
        startSymbol = symbols[index];
        startIndex = index;
      }
      if (dateStr === endDate) {
        endSymbol = symbols[index];
        endIndex = index;
      }
    });

    if (!startSymbol || !endSymbol || startIndex === -1 || endIndex === -1) {
      return;
    }

    const startX = parseFloat($(startSymbol.node).attr("cx") || "0");
    const endX = parseFloat($(endSymbol.node).attr("cx") || "0");

    // データからY軸の範囲を計算
    const allStats = series.stats();
    const minY = Math.min(...allStats);
    const maxY = Math.max(...allStats);
    const yRange = maxY - minY || 1;

    // 線形回帰の直線のY値を計算
    // interceptとslopeは、Seriesの最初の日付（startDate）をインデックス0として計算されている
    // したがって、startIndexを0として扱い、endIndex - startIndexを相対終了インデックスとして使用する
    const relativeStartIndex = 0;
    const relativeEndIndex = endIndex - startIndex;

    const startYValue = intercept + slope * relativeStartIndex;
    const endYValue = intercept + slope * relativeEndIndex;

    // グラフの座標系に変換
    // Y座標は下から上に増えるので、反転が必要
    const graphBottom = this.GRAPH_Y + this.GRAPH_HEIGHT - this.AXIS_SPACE;
    const graphTop = this.GRAPH_Y + this.AXIS_SPACE;
    const graphHeight = graphBottom - graphTop;

    const startY = graphBottom - ((startYValue - minY) / yRange) * graphHeight;
    const endY = graphBottom - ((endYValue - minY) / yRange) * graphHeight;

    // 灰色の線を描画
    this.paper
      .path(`M ${startX} ${startY} L ${endX} ${endY}`)
      .attr({
        stroke: "gray",
        "stroke-width": 1,
      });

    // ツールチップ用の透明な矩形を線に沿って配置
    const lineLength = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
    const lineAngle = Math.atan2(endY - startY, endX - startX);
    const centerX = (startX + endX) / 2;
    const centerY = (startY + endY) / 2;

    // 線に沿った透明な矩形を作成（線の幅を広めに取る）
    const tooltipWidth = lineLength;
    const tooltipHeight = 20; // マウスオーバーしやすいように高さを確保

    // 矩形を線の中央に配置し、線の角度に合わせて回転
    const tooltipRect = this.paper
      .rect(
        centerX - tooltipWidth / 2,
        centerY - tooltipHeight / 2,
        tooltipWidth,
        tooltipHeight,
      )
      .attr({
        fill: "transparent",
        stroke: "none",
        "stroke-width": 0,
        opacity: 0,
      })
      .rotate((lineAngle * 180) / Math.PI, centerX, centerY);

    // ツールチップのテキストを作成
    const annualSlopeText = annualSlope >= 0
      ? `+${annualSlope.toFixed(1)}%`
      : `${annualSlope.toFixed(1)}%`;

    // tipsyプラグインを使ってツールチップを追加
    $(tooltipRect.node).tipsy({
      gravity: "s",
      html: true,
      title: () => `線形回帰直線<br/>上昇率（年率換算）: ${annualSlopeText}`,
    });
  }

  protected getChartSymbols(chart: any): any[] {
    return chart.symbols;
  }

  /**
   * 前月と前年前月のノードを二重丸で囲む（比較対象を明確にするため）
   */
  private highlightComparisonNodes(): void {
    if (!this.chart || !this.chart.symbols || !this.paper) {
      return;
    }

    // 現在の日付から前月と前年前月を計算
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 1-12

    // 前月を計算
    let lastMonthYear = currentYear;
    let lastMonth = currentMonth - 1;
    if (lastMonth === 0) {
      lastMonth = 12;
      lastMonthYear = currentYear - 1;
    }

    // 前年前月を計算（前月の前年）
    const lastYearLastMonthYear = lastMonthYear - 1;
    const lastYearLastMonth = lastMonth;

    // 日付文字列を生成（YYYY-MM-DD形式、月初めの日付）
    const lastMonthStr = `${lastMonthYear}-${String(lastMonth).padStart(2, "0")}-01`;
    const lastYearLastMonthStr = `${lastYearLastMonthYear}-${String(lastYearLastMonth).padStart(2, "0")}-01`;

    // 各系列のシンボルを処理
    this.chart.symbols.forEach((symbols: any[], seriesIndex: number) => {
      const series = this.seriesSet.get(seriesIndex);
      if (!series) return;

      symbols.forEach((symbol: any) => {
        if (!symbol || !symbol.node) return;

        // シンボルの日付を取得
        const dateStr = $(symbol.node).attr("data-date");
        if (!dateStr) return;

        // 日付が前月または前年前月と一致するかチェック（月初めの日付で比較）
        const dateObj = new Date(dateStr);
        const symbolYear = dateObj.getFullYear();
        const symbolMonth = dateObj.getMonth() + 1;
        const symbolDateStr = `${symbolYear}-${String(symbolMonth).padStart(2, "0")}-01`;

        const isLastMonth = symbolDateStr === lastMonthStr;
        const isLastYearLastMonth = symbolDateStr === lastYearLastMonthStr;

        if (isLastMonth || isLastYearLastMonth) {
          // シンボルの位置を取得
          const cx = parseFloat($(symbol.node).attr("cx") || "0");
          const cy = parseFloat($(symbol.node).attr("cy") || "0");

          if (cx > 0 && cy > 0 && this.paper) {
            // 外側の円を描画（二重丸の外側）
            const outerRadius = StatsGraph.CHART_SYMBOL_SIZE + 4; // 元のシンボルサイズ + 4px
            const outerCircle = this.paper.circle(cx, cy, outerRadius);
            outerCircle.attr({
              stroke: this.seriesColor(seriesIndex),
              "stroke-width": 2,
              fill: "none",
            });

            // 外側の円をシンボルの下に配置（z-index的な処理）
            outerCircle.toBack();
          }
        }
      });
    });
  }

  /**
   * 今年から昨年の部分を破線にする
   */
  private makeCurrentYearToLastYearDashed(): void {
    if (!this.chart || !this.chart.lines || !this.paper) {
      return;
    }

    const currentDate = new Date();
    const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

    // 各系列の線を処理
    this.chart.lines.forEach((line: any, seriesIndex: number) => {
      const series = this.seriesSet.get(seriesIndex);
      if (!series) return;

      const dates = series.dates();
      const symbols = this.chart.symbols[seriesIndex];
      if (!symbols || symbols.length === 0) return;

      // 予測データの開始インデックスを特定（is_projectedフラグまたは現在の月以降）
      let projectedStartIndex = -1;

      for (let i = 0; i < dates.length; i++) {
        const date = dates[i];
        const dateObj = new Date(date.year(), date.month() - 1, 1); // DataDateのmonth()は1始まり

        // is_projectedフラグがある場合はそれを使用、ない場合は現在の月以降を判定
        if (series.isProjected(i)) {
          projectedStartIndex = i;
          break;
        } else if (dateObj >= currentMonth) {
          projectedStartIndex = i;
          break;
        }
      }

      // 予測データがない場合は何もしない
      if (projectedStartIndex === -1 || projectedStartIndex >= dates.length) {
        return;
      }

      // 2025年11月から12月にかけて破線にするため、11月から12月の間の線を破線にする
      // projectedStartIndexは12月のインデックスなので、11月のインデックス（projectedStartIndex - 1）から破線を開始
      const dashedStartIndex = Math.max(0, projectedStartIndex - 1);

      // 元の線の色と幅を取得
      const lineColor = line.attr("stroke");
      const lineWidth = line.attr("stroke-width");

      // シンボルの実際の位置を取得
      const getSymbolPosition = (symbol: any): { x: number; y: number } | null => {
        if (!symbol || !symbol.node) return null;
        try {
          const bbox = symbol.getBBox();
          if (bbox && bbox.width > 0 && bbox.height > 0) {
            return { x: bbox.x + bbox.width / 2, y: bbox.y + bbox.height / 2 };
          }
        } catch (_e) {
          // getBBox()が失敗した場合は、属性から取得
        }
        const cx = parseFloat($(symbol.node).attr("cx") || "0");
        const cy = parseFloat($(symbol.node).attr("cy") || "0");
        if (cx > 0 && cy > 0) {
          return { x: cx, y: cy };
        }
        return null;
      };

      // パスを構築するヘルパー関数
      const buildPath = (startIdx: number, endIdx: number): string | null => {
        let path = "";
        let isFirst = true;

        for (let i = startIdx; i <= endIdx; i++) {
          const symbol = symbols[i];
          if (!symbol) continue;

          const pos = getSymbolPosition(symbol);
          if (!pos) continue;

          if (isFirst) {
            path = `M ${pos.x} ${pos.y}`;
            isFirst = false;
          } else {
            path += ` L ${pos.x} ${pos.y}`;
          }
        }

        return path || null;
      };

      // 元の線を非表示にする
      line.hide();

      if (!this.paper) return;

      // 2つのセグメントを描画：
      // 1. 最初からdashedStartIndexまで（実線：11月までの実データ、10月から11月の間の線も含む）
      if (dashedStartIndex >= 0) {
        const pathBefore = buildPath(0, dashedStartIndex);
        if (pathBefore) {
          this.paper.path(pathBefore).attr({
            stroke: lineColor,
            "stroke-width": lineWidth,
            "stroke-linejoin": "round",
            "stroke-linecap": "round",
          });
        }
      }

      // 2. dashedStartIndex（11月）から最後まで（破線：11月から12月にかけての予測データ）
      if (dashedStartIndex < symbols.length - 1) {
        const dashedPath = buildPath(dashedStartIndex, symbols.length - 1);
        if (dashedPath) {
          this.paper.path(dashedPath).attr({
            stroke: lineColor,
            "stroke-width": lineWidth,
            "stroke-dasharray": "-", // 破線パターン
            "stroke-linejoin": "round",
            "stroke-linecap": "round",
          });
        }
      }
    });
  }

  private drawSerieses(seriesSet: SeriesSet): any {
    const chart = this.paper!.linechart(
      this.GRAPH_X,
      this.GRAPH_Y,
      this.GRAPH_WIDTH,
      this.GRAPH_HEIGHT,
      seriesSet.map((s) => s.originRelativeTimes()),
      seriesSet.map((s) => s.stats()),
      {
        axis: "0 0 1 1",
        colors: this.colors.slice(0, seriesSet.size()),
        axisxstep: this.xAxis.step(),
        axisystep: this.yAxis.step(),
        width: StatsGraph.CHART_LINE_WIDTH,
        symbol: "circle",
        "symbol-size": StatsGraph.CHART_SYMBOL_SIZE,
        showAllLabels: this.xAxis.doesShowAllLabels(),
      },
    );

    // Add data-date attributes
    chart.symbols.forEach((series: any[], seriesIndex: number) => {
      series.forEach((symbol: any, pointIndex: number) => {
        const dateValue = seriesSet.list[seriesIndex].dates()[pointIndex].ymd();
        $(symbol.node).attr("data-date", dateValue);
      });
    });
    return chart;
  }

  seriesColor(index: number): string {
    return this.colors[index];
  }

  private drawDivisions(): void {
    if (!this.paper) return;

    for (let d = 0; d < StatsGraph.NUM_DIVISIONS; d++) {
      this.paper
        .rect(
          this.GRAPH_X + this.AXIS_SPACE,
          this.GRAPH_Y +
          this.AXIS_SPACE +
          d *
          (this.GRAPH_HEIGHT / (StatsGraph.NUM_DIVISIONS + 1) +
            StatsGraph.DIVISION_STEP_ADJUST),
          this.GRAPH_WIDTH - 2 * this.AXIS_SPACE,
          StatsGraph.DIVISION_WIDTH,
        )
        .attr(StatsGraph.DIVISION_ATTRIBUTES);
    }
  }

  private drawTodayLine(posRatio: number): void {
    if (!this.paper) return;

    this.paper
      .rect(
        this.GRAPH_X + this.GRAPH_WIDTH * posRatio,
        this.GRAPH_Y + this.AXIS_SPACE,
        StatsGraph.TODAY_BAR_WIDTH,
        this.GRAPH_HEIGHT - 2 * this.AXIS_SPACE,
      )
      .attr(StatsGraph.TODAY_BAR_ATTRIBUTES);
  }

  private getOneExperienceStartPosition(): string | null {
    const targetDate = OneExperienceNotice.getStartDate(
      (this.xAxis as any).constructor,
    );
    if (!targetDate) return null;

    const matchingElement = $(`[data-date='${targetDate}']`);
    return matchingElement.attr("cx") || null;
  }

  private drawOneExperienceLine(): void {
    const x = this.getOneExperienceStartPosition();
    if (!x || !this.paper) return;

    const elm = this.paper
      .rect(
        parseFloat(x),
        this.GRAPH_Y + this.AXIS_SPACE,
        1,
        this.GRAPH_HEIGHT - 2 * this.AXIS_SPACE,
      )
      .attr({ "stroke-width": 0, fill: "#AAA" });

    // Tooltip (would require tipsy plugin)

    $(elm.node).tipsy({
      gravity: "s",
      html: true,
      title: () =>
        `cookpad.com リニューアル日<br />${OneExperienceNotice.START_DATE}`,
    });
  }

  private fixAxis(chart: any, seriesSet: SeriesSet): void {
    const x = chart.axis[0];
    if (x) {
      this.xAxis.fixLabels(x);
      this.setAxisAttributes(x);
    }

    const y = chart.axis[1];
    if (y) {
      this.yAxis.fixLabels(y, seriesSet);
      this.setAxisAttributes(y);
    }
  }

  private setAxisAttributes(axis: any): void {
    axis.attr(StatsGraph.AXIS_ATTRIBUTES);
    if (axis.text && axis.text.items) {
      axis.text.items.forEach((label: any) => {
        label.attr(StatsGraph.AXIS_ATTRIBUTES);
      });
    }
  }

  private setTooltips(symbols: any[], seriesSet: SeriesSet): void {
    // Would require tipsy plugin implementation

    symbols.forEach((symSet, seriesIndex) => {
      const series = seriesSet.toArray()[seriesIndex];
      if (!series) return;

      symSet.forEach((sym: any, pointIndex: number) => {
        const date = series.dates()[pointIndex];
        const stat = series.stats()[pointIndex];
        $(sym.node).tipsy({
          gravity: "s",
          html: true,
          title: () => this.tipString(date, stat, series),
        });
      });
    });
  }

  private tipString(date: any, val: number, series: Series): string {
    return `${date.ymd()}: ${this.formatStats(val)}<br/>${series.name}`;
  }

  private formatStats(val: number): string {
    if (val > 10) return val.toFixed(1);
    if (val > 1) return val.toFixed(2);
    return val.toFixed(3);
  }

  private drawLegends(): void {
    this.legend.draw();
  }

  // Legend implementations
  static TextLegend = class implements LegendInstance {
    private static readonly WIDTH = 90;
    private static readonly FONT_SIZE = "16";

    protected graph: StatsGraph;

    constructor(graph: StatsGraph) {
      this.graph = graph;
    }

    draw(): void {
      this.graph.seriesSet.toArray().forEach((series, index) => {
        this.drawText(this.graph.chart!, index, series.name);
      });
    }

    private drawText(chart: any, index: number, label: string): void {
      const color = this.graph.seriesColor(index);
      this.graph.paper!.text(this.x(index), this.y(chart, index), label).attr({
        stroke: color,
        fill: color,
        "font-size": StatsGraph.TextLegend.FONT_SIZE,
      });
    }

    protected x(index: number): number {
      return this.rightSide(index);
    }

    protected y(chart: any, index: number): number {
      return this.topToBottom(chart, index);
    }

    protected rightToLeft(index: number): number {
      return (
        this.graph.GRAPH_X +
        this.graph.GRAPH_WIDTH -
        StatsGraph.TextLegend.WIDTH * (index + 1)
      );
    }

    protected rightSide(_index: number): number {
      return this.graph.GRAPH_X + this.graph.GRAPH_WIDTH - 50;
    }

    protected leftOrRight(index: number): number {
      switch (index) {
        case 0:
          return this.graph.GRAPH_X + (this.graph.GRAPH_WIDTH - 80);
        case 1:
          return this.graph.GRAPH_X + (this.graph.GRAPH_WIDTH / 2 - 80);
        default:
          throw new Error(`bad data series index: ${index}`);
      }
    }

    protected topToBottom(chart: any, index: number): number {
      return 30 + index * 25;
    }

    protected nearByLastPoint(chart: any, index: number): number {
      const pos = this.lastPoint(chart.lines[index]);
      let y: number;
      if (pos) {
        [, y] = pos;
      } else {
        y = 0;
      }
      return (
        y +
        (y - this.graph.GRAPH_Y >
          (this.graph.GRAPH_HEIGHT - this.graph.GRAPH_Y) / 2
          ? -50
          : 50)
      );
    }

    protected lastPoint(line: any): [number, number] | null {
      const path = line.attr("path");
      if ((window as any).Raphael?.svg) {
        if (path.length === 0) return null;
        return path[path.length - 1].slice(1, 3);
      } else {
        // VML (IE)
        if (path === "") return null;
        return path.split(",").slice(-2).map(parseFloat) as [number, number];
      }
    }
  };

  // si graph
  static WrappedSeriesLegend = class extends StatsGraph.TextLegend {
    protected x(index: number): number {
      return this.leftOrRight(index);
    }

    protected y(chart: any, index: number): number {
      return this.nearByLastPoint(chart, index);
    }
  };

  // ksi graph
  static RightColumnLegend = class implements LegendInstance {
    private static readonly WIDTH = 130;

    private graph: StatsGraph;

    constructor(graph: StatsGraph) {
      this.graph = graph;
      this.graph.GRAPH_WIDTH -= StatsGraph.RightColumnLegend.WIDTH; // FIXME: do not update other object's attribute
    }

    draw(): void {
      this.graph.seriesSet.toArray().forEach((series, index) => {
        const pos = {
          x:
            this.graph.GRAPH_X + this.graph.GRAPH_WIDTH + this.graph.AXIS_SPACE,
          y: this.graph.AXIS_SPACE + index * 28,
        };
        this.drawRoundBox(pos, index);
        this.drawName(pos, series.name);
        this.drawCloseButton(pos, series);
      });
    }

    private drawRoundBox(pos: { x: number; y: number }, index: number): void {
      const m = {
        width: 110,
        height: 26,
        round: 8,
      };
      this.graph.paper!.rect(pos.x, pos.y, m.width, m.height, m.round).attr({
        "stroke-width": 0,
        fill: this.graph.seriesColor(index),
      });
    }

    private drawName(pos: { x: number; y: number }, name: string): void {
      const textElement = this.drawTextRelative(
        pos,
        { x: 8, y: 14 },
        this.shortenText(name, 6),
      ).attr({
        "text-anchor": "start",
        stroke: "white",
        "font-size": 14,
        cursor: "pointer"
      });

      // Raphaelパスで細い下線を描画
      const textBBox = textElement.getBBox();
      const underline = this.graph.paper!.path(
        `M ${textBBox.x} ${textBBox.y + textBBox.height - 1} L ${textBBox.x + textBBox.width} ${textBBox.y + textBBox.height - 1}`
      ).attr({
        stroke: "white",
        "stroke-width": 0.5
      });

      // キーワード分析ページへのリンク機能を追加
      const clickHandler = () => {
        const url = `/trends/${encodeURIComponent(name)}`;
        window.open(url, '_blank');
      };

      textElement.click(clickHandler);
      underline.click(clickHandler);
      underline.attr({cursor: "pointer"});
    }

    private drawCloseButton(
      pos: { x: number; y: number },
      series: Series,
    ): void {
      this.drawTextRelative(pos, { x: 87, y: 13 }, "×")
        .attr({
          "text-anchor": "start",
          stroke: "white",
          fill: "white",
          "font-size": 22,
          cursor: "pointer",
        })
        .click(() => this.graph.removeSeries(series));
    }

    private drawTextRelative(
      pos: { x: number; y: number },
      diffs: { x: number; y: number },
      text: string,
    ): RaphaelElement {
      return this.graph.paper!.text(pos.x + diffs.x, pos.y + diffs.y, text);
    }

    private shortenText(text: string, maxLen: number): string {
      if (text.length > maxLen) {
        return text.substring(0, maxLen - 1) + "…";
      } else {
        return text;
      }
    }
  };

  downloadAsPng(): void {
    if (!this.paper) {
      console.error("Graph is not drawn yet");
      return;
    }

    const svgElement = this.paper.canvas as SVGElement;
    downloadAsPng(svgElement, this.CANVAS_WIDTH, this.CANVAS_HEIGHT);
  }
}

export { StatsGraph };
